/**
 * The ONE rule for the human-readable name of a person (an auth user) on
 * shop surfaces — advisors, managers, technicians, custody actors.
 *
 * Canonical sources, in order:
 *   1. the shop roster name (`technicians.name` through `technician_users`) —
 *      the name the shop typed for its technician, readable by shop members;
 *   2. the professional identity (`technician_profiles.full_name`) — readable
 *      by the person and their managers only, so best effort;
 *   3. the account display name (`profiles.display_name`).
 *
 * An EMAIL is never a name. Signup writes `profiles.display_name =
 * coalesce(metadata.display_name, email)`, so for most accounts the stored
 * "display name" IS the email address; such a value — anything shaped like
 * an address, or equal to the account's email — counts as no name. No name is
 * ever derived from an email (no local-part guessing).
 *
 * When no source holds a real name the result is null, and the caller says
 * so ("Unknown member") — which then means OptiBay genuinely has no name for
 * the person, never that a lookup failed.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export const UNKNOWN_MEMBER_LABEL = 'Unknown member';

const EMAIL_SHAPE = /[^\s@]+@[^\s@]+\.[^\s@]+/;

export function looksLikeEmail(value: string | null | undefined): boolean {
  return !!value && EMAIL_SHAPE.test(value);
}

export interface PersonNameSources {
  rosterName?: string | null;
  professionalName?: string | null;
  profileDisplayName?: string | null;
  /** The account email, only to reject a "name" that is in fact the email. */
  email?: string | null;
}

/** The first real name among the canonical sources, or null. Pure. */
export function pickPersonDisplayName(s: PersonNameSources): string | null {
  const email = s.email?.trim().toLowerCase() || null;
  for (const raw of [s.rosterName, s.professionalName, s.profileDisplayName]) {
    const v = raw?.trim();
    if (!v) continue;
    if (looksLikeEmail(v)) continue;
    if (email && v.toLowerCase() === email) continue;
    return v;
  }
  return null;
}

/** The longest human name a person record accepts. */
export const MAX_PERSON_NAME_LENGTH = 80;

/**
 * Why `name` cannot be stored as a person's human name, or null when it can.
 * The one rule every place that WRITES a name uses (invitation signup, Team
 * Management, Account Settings): a name is required, is not an email address
 * (nor the account's own email), and is of human length. Nothing is ever
 * derived from the email — the person or their manager types the name.
 */
export function validatePersonName(name: string | null | undefined, email?: string | null): string | null {
  const v = (name ?? '').trim();
  if (!v) return "Enter the person's name.";
  if (looksLikeEmail(v) || (email && v.toLowerCase() === email.trim().toLowerCase())) {
    return 'A name cannot be an email address.';
  }
  if (v.length > MAX_PERSON_NAME_LENGTH) return `Keep the name under ${MAX_PERSON_NAME_LENGTH} characters.`;
  return null;
}

type Row = Record<string, unknown>;

/**
 * Resolve real names for a set of user ids. Returns ONLY resolved names; an id
 * with no real name is absent. Every read is one the caller's shop membership
 * may make; a failed read contributes nothing (the next source is still
 * tried), it never turns into an email or a guessed name.
 *
 * `shopId` narrows the roster binding to that shop (a person can be bound in
 * several shops; their name here is this shop's roster name).
 */
export async function fetchPersonDisplayNames(
  client: SupabaseClient | unknown,
  userIds: ReadonlyArray<string | null | undefined>,
  shopId?: string | null,
): Promise<Record<string, string>> {
  const db = client as SupabaseClient;
  const ids = [...new Set(userIds.filter((x): x is string => typeof x === 'string' && x.length > 0))];
  if (ids.length === 0) return {};
  const sources = new Map<string, PersonNameSources>(ids.map((id) => [id, {}]));

  // 1. roster: technician_users → technicians.name
  try {
    let q = (db as any).from('technician_users').select('user_id, technician_id, shop_id').in('user_id', ids);
    if (shopId) q = q.eq('shop_id', shopId);
    const { data: bindings } = await q;
    const techIds = [...new Set(((bindings ?? []) as Row[]).map((b) => b.technician_id as string).filter(Boolean))];
    if (techIds.length > 0) {
      const { data: techs } = await (db as any).from('technicians').select('id, name').in('id', techIds);
      const nameByTech = new Map(((techs ?? []) as Row[]).map((t) => [t.id as string, (t.name as string | null) ?? null]));
      for (const b of (bindings ?? []) as Row[]) {
        const s = sources.get(b.user_id as string);
        if (s && !s.rosterName) s.rosterName = nameByTech.get(b.technician_id as string) ?? null;
      }
    }
  } catch { /* unreadable roster: next source */ }

  // 2. professional identity (self / manager-readable only)
  try {
    const { data: tp } = await (db as any).from('technician_profiles').select('user_id, full_name').in('user_id', ids);
    for (const t of (tp ?? []) as Row[]) {
      const s = sources.get(t.user_id as string);
      if (s && !s.professionalName) s.professionalName = (t.full_name as string | null) ?? null;
    }
  } catch { /* not readable for this viewer: next source */ }

  // 3. account display name (and the email, only to reject it as a "name")
  try {
    const { data: profiles } = await (db as any).from('profiles').select('id, display_name, email').in('id', ids);
    for (const p of (profiles ?? []) as Row[]) {
      const s = sources.get(p.id as string);
      if (s) { s.profileDisplayName = (p.display_name as string | null) ?? null; s.email = (p.email as string | null) ?? null; }
    }
  } catch { /* unreadable: nothing from this source */ }

  const out: Record<string, string> = {};
  for (const [id, s] of sources) {
    const name = pickPersonDisplayName(s);
    if (name) out[id] = name;
  }
  return out;
}
